/* eslint-disable react/prop-types */
import { useCallback } from 'react';

import { DesktopUserDialogModal } from 'app/components/modals/desktop-user-dialog-modal';
import { MobileUserDialogModal } from 'app/components/modals/mobile-user-dialog-modal';
import { useAttachable } from 'app/components/attachable/hooks/use-attachable';
import { useAttachableText } from 'app/components/attachable/hooks/use-attachable-text';
import { TenantContentData } from 'app/data/types/content';
import { UserDialogButton } from 'app/components/modals/desktop-user-dialog-modal/desktop-user-dialog-modal';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';

interface AttachableProps {
  attachable: TenantContentData;
  isMobile?: boolean;
}

export const Attachable: React.FC<AttachableProps> = ({ attachable, isMobile }): JSX.Element => {
  const {
    isVisible,
    isConfirmedModalVisible,
    onCancelClick,
    buttonIsLoading,
    onClickButton,
    onCtaClick,
    isClaimable,
    isExternalUrl,
    hasOpenExternalLink,
    updateEventPostStatus,
    updateOfferStatus,
    errorMessage,
    ctaUrl,
    showCancelClaimButton,
    onCancelClaimClick,
    showCancelTextOverride,
  } = useAttachable({
    attachable,
    isMobile,
  });

  const {
    buttonYesText,
    buttonCancelText,
    descriptionText,
    label,
    titleText,
    isButtonDisabled,
    isButtonHidden,
    cancelClaimButtonText,
    isFooterHidden,
  } = useAttachableText({
    attachable,
    isConfirmedModalVisible,
    isClaimable,
    updateEventPostStatus,
    updateOfferStatus,
    showCancelTextOverride,
  });

  const getButtons = useCallback(() => {
    const buttons = [
      { title: buttonCancelText, handler: onCancelClick, variant: 'secondary' },
      { title: buttonYesText, isLoading: buttonIsLoading, handler: onClickButton },
    ];
    if (isConfirmedModalVisible) {
      return [buttons[1]];
    }
    return buttons;
  }, [buttonCancelText, buttonIsLoading, buttonYesText, isConfirmedModalVisible, onClickButton, onCancelClick]);

  const shadowClasses = 'shadow-[0px_6px_8px_rgba(45,51,56,0.12),0px_0px_3px_rgba(45,51,56,0.1)]';
  const footerClasses = `fixed bottom-0 left-0 right-0 bg-white px-4 py-4 flex flex-row items-center justify-center gap-2 ${shadowClasses}`;

  return (
    <>
      {
        isMobile ? (
          <MobileUserDialogModal
            buttons={getButtons() as UserDialogButton[]}
            description={descriptionText}
            isVisible={isVisible}
            onClose={onCancelClick}
            title={titleText}
            errorMessage={errorMessage}
            isConfirmation={isConfirmedModalVisible}
          />
        ) : (
          <DesktopUserDialogModal
            buttons={getButtons() as UserDialogButton[]}
            description={descriptionText}
            isVisible={isVisible}
            onClose={onCancelClick}
            title={titleText}
            errorMessage={errorMessage}
            isConfirmation={isConfirmedModalVisible}
          />
        )
      }
      <div className={cn(footerClasses, isMobile && showCancelClaimButton && !isButtonHidden && 'grid grid-cols-[50%_50%]', isFooterHidden && 'hidden')}>
        <Button
          onClick={onCtaClick}
          variant="default"
          disabled={isButtonDisabled}
          hidden={isButtonHidden}
          className="h-14 w-full md:w-auto md:min-w-100 font-normal text-lg disabled:opacity-50 disabled:cursor-not-allowed text-wrap whitespace-normal"
          data-testid="attachable-button"
        >
          { isExternalUrl && isMobile && !hasOpenExternalLink ? (
            <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="flex justify-center items-center w-full h-full bg-transparent">
              {label}
            </a>
          ) : (
            label
          )}
        </Button>
        {showCancelClaimButton && (
          <Button
            onClick={onCancelClaimClick}
            variant="ghost"
            className={cn(
              'h-14 w-full md:w-auto md:min-w-100 font-normal text-lg hover:bg-system-grey-light text-white hover:text-white px-1 text-wrap whitespace-normal bg-system-pink hover:bg-system-pink-hover',
            )}
            data-testid="attachable-cancel-button"
          >
            {cancelClaimButtonText}
          </Button>
        )}
      </div>
    </>
  );
};
